import { useState, useEffect, useRef } from "react";
import useMaterialsMasterDataInit from "./useMaterialsMasterDataInit";
import { useMaterialUpdate } from "./useMaterialUpdate";
import { useDatabaseSync } from "./useDatabaseSync";

/**
 * useMaterials (Orchestrator)
 * ---------------------------
 *   1. useMaterialsMasterDataInit → loads data from server
 *   2. useMaterialUpdate          → pure state updates on user actions
 *   3. useDatabaseSync (via useEffect) → syncs selectedValues to DB after every real change
 */
export function useMaterials({ measurements }: { measurements: any }) {
    // ── State ────────────────────────────────────────────────────────────────
    const [materialSchema, setMaterialSchema] = useState<any[]>([]);
    const [cart, setCart] = useState<any[]>([]);
    const [priceRanges, setPriceRanges] = useState<Record<string, { min: number; max: number }>>({});
    const [identifiedProducts, setIdentifiedProducts] = useState<any>(null);
    const [missingProducts, setMissingProducts] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedValues, setSelectedValues] = useState<Record<string, any>>({});
    const [openAdditionalProductDialog, setOpenAdditionalProductDialog] = useState(false);

    // ── Sync ─────────────────────────────────────────────────────────────────
    const { initializeDbState, syncToDatabase } = useDatabaseSync();
    const syncReadyRef = useRef(false); // Prevents sync from firing on hydration

    useEffect(() => {
        if (!syncReadyRef.current) return;
        void syncToDatabase(selectedValues);
    }, [selectedValues]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Hook: Update ─────────────────────────────────────────────────────────
    const { handleQuantityUpdate, handleClearProduct, handleProductChange, handleColorChange } = useMaterialUpdate({
        materialSchema,
        measurements,
        setSelectedValues,
    });

    // ── Hook: Init ───────────────────────────────────────────────────────────
    const { loadMasterData, fetchCartItems, calculatePriceRanges } = useMaterialsMasterDataInit({
        measurements,
        materialSchema,
        setMaterialSchema,
        setCart,
        setPriceRanges,
        setIdentifiedProducts,
        setMissingProducts,
        setIsLoading,
        setSelectedValues: (values) => {
            setSelectedValues(values);
            initializeDbState(values);
            syncReadyRef.current = true; // From now on, all changes will sync
        },
    });

    // ── Expose ───────────────────────────────────────────────────────────────
    return {
        materialSchema,
        cart,
        priceRanges,
        identifiedProducts,
        missingProducts,
        isLoading,
        selectedValues,
        openAdditionalProductDialog,
        setOpenAdditionalProductDialog,
        loadMasterData,
        fetchCartItems,
        calculatePriceRanges,
        handleQuantityUpdate,
        handleClearProduct,
        handleProductChange,
        handleColorChange,
    };
}
